/**
 * Unified image extraction from slot payloads.
 * Handles both DCA (imageSrc, imageUrls) and CF (assetPath) structures.
 */

const DCA_IMAGE_KEYS = ['image', 'logo', 'tertiaryImage'] as const;
export type DcaImageKey = (typeof DCA_IMAGE_KEYS)[number];

function isRecord(obj: unknown): obj is Record<string, unknown> {
  return obj !== null && typeof obj === 'object' && !Array.isArray(obj);
}

function isDcaImageNode(node: unknown): node is Record<string, unknown> {
  if (!isRecord(node)) return false;
  return (
    node['imageSrc'] != null ||
    (isRecord(node['imageUrls']) &&
      (node['imageUrls']['square'] != null || node['imageUrls']['standardHorizontal'] != null))
  );
}

function getBaseUrlFromDcaNode(node: Record<string, unknown>): string {
  const imageUrls = isRecord(node['imageUrls']) ? node['imageUrls'] : null;
  return (
    (typeof node['imageSrc'] === 'string' ? node['imageSrc'] : '') ||
    (typeof imageUrls?.['square'] === 'string' ? imageUrls['square'] : '') ||
    (typeof imageUrls?.['standardHorizontal'] === 'string' ? imageUrls['standardHorizontal'] : '') ||
    ''
  );
}

function getAltFromDcaNode(node: Record<string, unknown>): string {
  return (typeof node['alternateDescription'] === 'string' ? node['alternateDescription'] : '') || '';
}

/**
 * Extracts base URL for image from payload.
 * DCA: imageSrc OR imageUrls.square OR imageUrls.standardHorizontal (in that order)
 * CF: assetPath
 * When fromKey is provided, extract from that DCA node (logo/image/tertiaryImage) first; fall back to CF.
 */
export function getBaseUrlFromPayload(payload: Record<string, unknown>, fromKey?: DcaImageKey): string {
  if (fromKey) {
    const node = payload[fromKey];
    if (isDcaImageNode(node)) return getBaseUrlFromDcaNode(node);
    return (typeof payload['assetPath'] === 'string' ? payload['assetPath'] : '') || '';
  }
  const imageObj = isRecord(payload['image']) ? payload['image'] : null;
  const imageUrls = imageObj && isRecord(imageObj['imageUrls']) ? imageObj['imageUrls'] : null;
  const dcaImageUrl =
    (typeof imageObj?.['imageSrc'] === 'string' ? imageObj['imageSrc'] : '') ||
    (typeof imageUrls?.['square'] === 'string' ? imageUrls['square'] : '') ||
    (typeof imageUrls?.['standardHorizontal'] === 'string' ? imageUrls['standardHorizontal'] : '') ||
    '';
  return (typeof payload['assetPath'] === 'string' ? payload['assetPath'] : '') || dcaImageUrl || '';
}

/**
 * Extracts alt text for image from payload.
 * CF: altText OR imageAltText (payload top-level)
 * DCA: imageAltText (payload top-level) OR image.alternateDescription
 * When fromKey is provided, extract from that DCA node first; fall back to CF.
 */
export function getAltFromPayload(payload: Record<string, unknown>, fromKey?: DcaImageKey): string {
  if (fromKey) {
    const node = payload[fromKey];
    if (isDcaImageNode(node)) return getAltFromDcaNode(node);
    return (
      (typeof payload['imageAltText'] === 'string' ? payload['imageAltText'] : '') ||
      (typeof payload['altText'] === 'string' ? payload['altText'] : '') ||
      ''
    );
  }
  const imageObj = isRecord(payload['image']) ? payload['image'] : null;
  return (
    (typeof payload['imageAltText'] === 'string' ? payload['imageAltText'] : '') ||
    (typeof payload['altText'] === 'string' ? payload['altText'] : '') ||
    (typeof imageObj?.['alternateDescription'] === 'string' ? imageObj['alternateDescription'] : '') ||
    ''
  );
}

/**
 * Extracts baseUrl and alt from payload in one call.
 * When fromKey is provided (e.g. 'logo'), extract from that DCA node first; fall back to CF.
 */
export function getBaseUrlAndAltFromPayload(
  payload: Record<string, unknown>,
  fromKey?: DcaImageKey
): { baseUrl: string; alt: string } {
  return {
    baseUrl: getBaseUrlFromPayload(payload, fromKey),
    alt: getAltFromPayload(payload, fromKey),
  };
}
